import { EventEmitter } from "@angular/core";
import { BehaviorSubject, defer, from, Observable } from "rxjs";
import { mergeAll, toArray } from 'rxjs/operators';

export class TableRecActionResult<RecordType> {
    constructor(
        public rec: RecordType,
        public ok: boolean,
        public msg: string
    ) { }
}

export class TableRecActionOkResult<RecordType> extends TableRecActionResult<RecordType> {
    constructor(rec: RecordType, msg: string = 'OK') {
        super(rec,true, msg)
    }
}

export class TableRecsActionResult<RecordType> {
    constructor(
        public ok: boolean,
        public msg: string,
        public results: TableRecActionResult<RecordType>[] = [],
        public context : any = null
    ) { }
}


export class TableRecsActionOkResult<RecordType> extends TableRecsActionResult<RecordType> {
    constructor(msg: string = 'OK', results: TableRecActionResult<RecordType>[] = []) {
        super(true, msg, results)
    }
}

export type TableRecArgs<RecordType,ContextType=any> = {
    rec: RecordType;
    action: TableRecsAction<RecordType,ContextType>;
}

export type TableRecsArgs<RecordType,ContextType=any> = {
    recs: RecordType[];
    action: TableRecsAction<RecordType,ContextType>;
}

export class TableRecsAction<RecordType,ContextType=any> {
    isBulkable = true
    isProcessing = false
    cssClass: string;
    tooltipText: string = '';
    running$ = new BehaviorSubject<RecordType[]>([])
    recComplete$ = new EventEmitter<TableRecActionResult<RecordType>>()

    constructor(
        public title: string,
        protected recsActionImpl: (args: TableRecsArgs<RecordType,ContextType>) => Promise<TableRecsActionResult<RecordType>>,
        public context?: ContextType
    ) { }

    async recsAction(args: TableRecsArgs<RecordType,ContextType>) {
        this.isProcessing = true
        try {
            if ( this.onBegin ) {
                const bContinue = await this.onBegin(args);               
                if ( !bContinue ) {
                    return new TableRecsActionOkResult(); // user cancelled action so don't continue (not an error)
                }
            }
            const result = await this.recsActionImpl(args)
            this.onComplete && this.onComplete({...args,result})
            return result
        } finally {
            this.isProcessing = false
        }
    }

    setCssClass( cssClass: string ) {
        this.cssClass = cssClass;
        return this;
    }

    onBegin: (args: TableRecsArgs<RecordType,ContextType>) => Promise<boolean>
    setOnBegin( onBegin: (args: TableRecsArgs<RecordType,ContextType>) => Promise<boolean> ) {
        this.onBegin = onBegin
        return this
    }

    onComplete: (args: TableRecsArgs<RecordType,ContextType>&{result: TableRecsActionResult<RecordType>}) => void
    setOnComplete( onComplete: (args: TableRecsArgs<RecordType,ContextType>&{result: TableRecsActionResult<RecordType>}) => void ) {
        this.onComplete = onComplete
        return this
    }

    // flag to allow a bulk action to show up in table header on left (otherwise it only shows in each row's actions dropdown)
    setBulkable(isBulkable:boolean){
        this.isBulkable = isBulkable
        return this
    }
}

// use this if a table bulk action should go row by row instead of all at once
export class TableRecsSequentialAction<RecordType,ContextType=any> extends TableRecsAction<RecordType,ContextType>{
    constructor(
        title: string,
        protected recActionFn: (args: TableRecArgs<RecordType,ContextType>) => Promise<TableRecActionResult<RecordType>>,
        context?: ContextType
    ) {
        super(title, null, context)
        this.recsActionImpl = this.doSequencial
    }

    doSequencial = async ({recs,action}: TableRecsArgs<RecordType,ContextType>) => {
        const results: TableRecActionResult<RecordType>[] = []
        if ( recs.length > 1 ) {
            recs.forEach(rec=>rec['__action_queued'] = this.title)
        }
        for (const rec of recs) {
            try{
                delete rec['__action_queued']
                const runningRecs = this.running$.value;
                if ( !runningRecs.find(r=>r===rec) ) {
                    this.running$.next([...runningRecs,rec]);
                }
                const result = await this.recActionFn({rec, action})
                this.recComplete$.emit(result)
                results.push(result)
            } catch ( error : any ) {
                console.error(error)
                const result = new TableRecActionResult(rec,false,error?.message||error)
                this.recComplete$.emit(result)
                results.push(result)
            }
        }
        this.running$.next([])
        const failedResults = results.filter(result => !result.ok)
        const allOk = failedResults.length == 0
        const msg = failedResults.map(r => r.msg).join(' | ')
        return new TableRecsActionResult(allOk, msg, results)
    }
}

export class TableRecsParallelAction<RecordType,ContextType=any> extends TableRecsAction<RecordType,ContextType>{
    constructor(
        title: string,
        protected recActionFn: (args: TableRecArgs<RecordType,ContextType>) => Promise<TableRecActionResult<RecordType>>,
        context?: ContextType
    ) {
        super(title, null, context)
        this.recsActionImpl = this.doParallel
    }

    maxConcurrent = 100
    setMaxConcurrent( maxConcurrent: number ) {
        this.maxConcurrent = maxConcurrent
        return this
    }

    doParallel = async ({recs,action}: TableRecsArgs<RecordType,ContextType>) => {
        if ( recs.length > 1 ) {
            recs.forEach(rec=>rec['__action_queued'] = this.title)
        }
        const parallelRecs = [...recs]
        const recFn = async (rec:RecordType) => {
            try {
                delete rec['__action_queued']
                const runningRecs = this.running$.value;
                if ( !runningRecs.find(r=>r===rec) ) {
                    runningRecs.push(rec);
                    this.running$.next(runningRecs);
                }
                const result = await this.recActionFn({rec, action})
                this.recComplete$.emit(result)
                return result
            } catch ( err : any ) {
                return new TableRecActionResult(rec,false,err?.message||err)
            } finally {
                const runningRecs = this.running$.value;
                const index = runningRecs.findIndex(r=>r==rec);
                if ( index != -1 ) {
                    runningRecs.splice(index,1);
                    this.running$.next(runningRecs);
                }
            }
        }
        const runningRecsResults$: Observable<TableRecActionResult<RecordType>>[] = parallelRecs.map(rec => defer(() => recFn(rec)))
        const allObservable = from(runningRecsResults$).pipe(mergeAll(this.maxConcurrent)).pipe(toArray())
        const results = await allObservable.toPromise()
        const failedResults = results.filter(result => !result.ok)
        const allOk = failedResults.length == 0
        const msg = failedResults.map(r => r.msg).join(' | ')
        return new TableRecsActionResult(allOk, msg, results)
    }
}
